import { ApolloQueryResult } from '@apollo/client';
import { GetRawArticleQuery_query_rawArticle_RawArticle } from '../../../__generated__/queries-topic';
import {
  ArticleFormat,
  BrandKey,
  CategoryFormat,
  GetArticleQuery_query_article_Article,
  GetArticleQuery_query_article_Article_categories_ArticleCategory,
  GetArticleQuery_query_article_Article_categories_ArticleCategory_category_Category,
  GetArticleQuery_query_article_Article_tags_ArticleTag,
  GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag,
  GetSuggestedCategoryQuery,
  GetSuggestedCategoryQuery_query_suggestCategories_Category_children_Category,
  GetSuggestedTagsListQuery,
  TagType,
  ArticleLikeInput,
} from '../../../__generated__/queries-web';
import { useSearchSuggestedTagsGetterPromise } from '../../../apollo/queries/getSuggestedTagsList.web.graphql';
import { useSuggestedCategoriesGetter } from '../../../apollo/queries/suggestCategories.web.graphql';
import { useRawArticleGetter } from '../queries/getSubjectQuery.topic.graphql';

import { brandsOptions } from '../../../utils/brands';
import { createArticleData } from '../utils/emptyTypes';
import { getSuggestedTagsArticleParams } from '../../../components/Tags/utils';

const mapOrderedRelationEntity = <T, E, U>({
  from,
  to,
  items,
  top,
  map,
}: {
  from: string;
  to: string;
  items: T[];
  top: number;
  map: (item: T) => Partial<U>;
}) =>
  items.slice(0, top).map(
    (item, order) =>
      (({
        __typename: `${from}${to}`,
        order,
        [to.toLowerCase()]: ({
          __typename: to,
          ...map(item),
        } as any) as U,
      } as any) as E),
  );

const getTopSuggestTags = <
  T extends { id: string | null; title: string; type: TagType }
>(
  items: T[] = [],
  top = 5,
) =>
  mapOrderedRelationEntity<
    T,
    GetArticleQuery_query_article_Article_tags_ArticleTag,
    GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag
  >({
    items,
    from: 'Article',
    to: 'Tag',
    top,
    map: (item) => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: item.id!,
      title: item.title,
      type: item.type,
    }),
  });

async function assertQueryResult<
  T extends ApolloQueryResult<any> | PromiseLike<ApolloQueryResult<any>>
>(r: T): Promise<PromiseOf<T>['data']> {
  const { errors = [], data } = await Promise.resolve(r);
  if (errors.length) {
    throw errors[0];
  } else {
    return data;
  }
}

const assertRawArticle = (
  id: string,
  article: any,
): NonNullableKeys<GetRawArticleQuery_query_rawArticle_RawArticle> => {
  if (!article) {
    throw Error(`Il n'existe pas un article avec l'identifiant ${id}.`);
  }

  if (!article.assignment) {
    throw Error(`Un probleme est détecté avec cet article.`);
  }

  return article;
};

const initWebArticleEntity = (
  articleType: string,
  brandKey: BrandKey,
  rawArticle: NonNullableKeys<GetRawArticleQuery_query_rawArticle_RawArticle>,
) => {
  const {
    versions: [{ body = '', title = '', lead = '' }],
    assignment: { id: assignmentId },
  } = rawArticle;
  return createArticleData(assignmentId, articleType, {
    brandKey,
    body,
    title: title ?? '',
    lead,
  });
};

export const useDuplicateArticle = async ({
  rawArticleId,
  versionId,
  articleType,
  brandKey,
}: {
  rawArticleId: string;
  versionId: string;
  articleType: ArticleFormat;
  brandKey: BrandKey;
}): Promise<{
  sourceArticle: NonNullableKeys<
    GetRawArticleQuery_query_rawArticle_RawArticle
  >;
  webArticle: GetArticleQuery_query_article_Article;
}> => {
  const getRawArticle = useRawArticleGetter();
  const getSuggestedTags = useSearchSuggestedTagsGetterPromise();
  const getSuggestedCategories = useSuggestedCategoriesGetter();

  const { rawArticle } = await assertQueryResult(
    getRawArticle(rawArticleId, versionId),
  );

  const sourceArticle = assertRawArticle(rawArticleId, rawArticle);
  const webArticle = {
    ...initWebArticleEntity(articleType, brandKey, sourceArticle),
    body: JSON.parse(
      initWebArticleEntity(articleType, brandKey, sourceArticle).body || '{}',
    ),
    lead: JSON.parse(
      initWebArticleEntity(articleType, brandKey, sourceArticle).lead || '{}',
    ),
  };

  if (!(webArticle.title || webArticle.lead || webArticle.body))
    throw Error(
      "Le contenu de l'article est vide, veuillez sélectionner un autre article",
    );

  const articleLikeInput: ArticleLikeInput = {
    title: webArticle.title,
    body: webArticle.body,
    lead: webArticle.lead,
  };

  const suggestionQueries = [
    getSuggestedTags({
      article: getSuggestedTagsArticleParams(articleLikeInput),
      where: {
        type: TagType.Tag,
      },
    }),
    getSuggestedTags({
      article: getSuggestedTagsArticleParams(articleLikeInput),
      where: {
        type: TagType.Person,
      },
    }),
    ...[CategoryFormat.Category, CategoryFormat.Event].map(() =>
      getSuggestedCategories({
        article: getSuggestedTagsArticleParams(articleLikeInput),
        first: 2,
      }),
    ),
  ].map(assertQueryResult);

  const [
    { suggestTags },
    { suggestTags: suggestPersonTags },
    { suggestCategories },
    { suggestCategories: suggestEvents },
  ] = await Promise.all<
    [
      GetSuggestedTagsListQuery,
      GetSuggestedTagsListQuery,
      GetSuggestedCategoryQuery,
      GetSuggestedCategoryQuery,
    ]
  >(suggestionQueries as any);

  const tags: GetArticleQuery_query_article_Article_tags_ArticleTag[] = [
    ...getTopSuggestTags(suggestTags, 3),
    ...getTopSuggestTags(suggestPersonTags, 1),
    ...getTopSuggestTags(
      suggestEvents.map((e: any) => ({
        ...e,
        type: 'Event' as TagType,
      })),
      1,
    ),
  ];

  const topCategoriesCount =
    Number(
      !!brandsOptions[brandKey].draft?.richFields.find(
        (o) => o === 'firstCategory',
      ),
    ) +
    Number(
      !!brandsOptions[brandKey].draft?.richFields.find(
        (o) => o === 'secondCategory',
      ),
    );

  const categories = mapOrderedRelationEntity<
    GetSuggestedCategoryQuery_query_suggestCategories_Category_children_Category,
    GetArticleQuery_query_article_Article_categories_ArticleCategory,
    GetArticleQuery_query_article_Article_categories_ArticleCategory_category_Category
  >({
    items: suggestCategories[0].children,
    from: 'Article',
    to: 'Category',
    top: topCategoriesCount,
    map: ({ id, title }) => ({
      id,
      title,
    }),
  });

  return {
    sourceArticle,
    webArticle: {
      ...webArticle,
      tags,
      categories,
    },
  };
};
